import { THEME } from '@common/constants/theme.constant';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Readonly } from '@common/general-ui/readonly.component';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { css } from 'twin.macro';

export type ORSwitchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => void;

export interface IORSwitchProps<TName = string> extends IFieldProps<boolean, TName> {
    onChange?: ORSwitchChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    childrenLeft: ReactNode;
    childrenRight: ReactNode;
    className?: string;
}

export const ORSwitch = (props: IORSwitchProps): JSX.Element => {
    const { onChange, childrenLeft: children1, childrenRight: children2 } = props;
    const [checked, setChecked] = useState(props.value);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (props.readOnly) {
                return;
            }

            setChecked(e.target.checked);

            onChange &&
                onChange({
                    ...e,
                    target: {
                        ...e.target,
                        value: e.target.value,
                    },
                });
        },
        [onChange, props.readOnly],
    );

    useEffect(() => {
        setChecked(props.value);
    }, [props.value]);

    const activeChildren = checked ? children2 : children1;
    return (
        <Field {...props} tw="relative">
            <label htmlFor={`toggle-${props.name}`} data-testid={props.name} css={styles.root} className={props.className}>
                <input
                    id={`toggle-${props.name}`}
                    type="checkbox"
                    className="input"
                    checked={checked}
                    onChange={handleChange}
                />
                <div className="toggle-wrapper">
                    {props.readOnly && <Readonly />}
                    <div tw="flex justify-between">
                        <div tw="flex-grow relative">
                            <div tw="px-2 whitespace-nowrap">{children1}</div>
                            <div className="selector">{activeChildren}</div>
                        </div>
                        <div tw="px-2 whitespace-nowrap">{children2}</div>
                    </div>
                </div>
            </label>
        </Field>
    );
};

const styles = {
    root: css`
        display: inline-block;
        pointer-events: none;
        font-size: ${THEME.BASE_FONT_SIZE};
        .input {
            display: none;
            &:checked + .toggle-wrapper {
                box-shadow: ${THEME.BOX_SHADOW_GREEN};
            }
            &:checked + .toggle-wrapper .selector {
                left: calc(100% + 6px);
                top: -3px;
            }
        }
        .toggle-wrapper {
            margin-left: 4px;
            padding: 4px 12px;
            position: relative;
            background-color: white;
            border-radius: 16px;
            cursor: pointer;
            pointer-events: all;
            color: ${THEME.GREEN};
            box-shadow: ${THEME.BOX_SHADOW_GREEN};

            .selector {
                position: absolute;
                white-space: nowrap;
                width: min-content;
                padding: 3px 14px;
                top: -3px;
                left: -14px;
                background-color: ${THEME.GREEN};
                transition: left 0.25s ease;
                border-radius: 16px;
                color: white;
            }
        }
    `,
};
